import type {
  HealthCheckStatus,
  PlaybookNodeSampleToolResultOutcomeHealthCheck,
} from "@repo/app-types/entities/playbookNodeSampleToolResult";
import {
  type DropdownItem,
  DropdownMenu,
} from "@repo/ui/Dropdown/dropdownMenu";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { IconCircleCheck, IconCircleX, IconDots } from "@tabler/icons-react";
import { type FC, type ReactNode, useState } from "react";
import { Button as AriaButton, MenuTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";

type OutcomeHealthCheckKey =
  keyof PlaybookNodeSampleToolResultOutcomeHealthCheck;

const HEALTH_CHECK_ROWS: readonly {
  key: OutcomeHealthCheckKey;
  label: string;
}[] = [
  { key: "grading_clarity", label: "Clear and consistent rubric" },
  {
    key: "transcript_observability",
    label: "Sufficient context for AI grading",
  },
];

interface OutcomeHealthCheckRow {
  key: OutcomeHealthCheckKey;
  label: string;
  status: HealthCheckStatus;
  explanation: string;
}

interface OutcomeHealthCheckSummary {
  rows: readonly OutcomeHealthCheckRow[];
  passed: number;
  failed: number;
}

/**
 * Flatten the outcome health check into ordered rows plus pass/fail tallies.
 * `partial` counts as a failure; `not_applicable` counts as passed. Pure so it
 * can be unit-tested without rendering.
 */
export function summarizeOutcomeHealthCheck(
  healthCheck: PlaybookNodeSampleToolResultOutcomeHealthCheck,
): OutcomeHealthCheckSummary {
  const rows = HEALTH_CHECK_ROWS.map(({ key, label }) => ({
    key,
    label,
    status: healthCheck[key].status,
    explanation: healthCheck[key].explanation,
  }));

  let passed = 0;
  let failed = 0;
  for (const { status } of rows) {
    if (status === "fail" || status === "partial") failed += 1;
    else passed += 1;
  }

  return { rows, passed, failed };
}

function statusIcon(status: HealthCheckStatus): ReactNode {
  if (status === "fail" || status === "partial") {
    return <IconCircleX className="size-5 shrink-0 text-red-600" />;
  }
  return <IconCircleCheck className="size-5 shrink-0 text-green-600" />;
}

const RING_SIZE = 28;
const RING_STROKE = 4;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const RingGlyph: FC<{ passed: number; failed: number }> = ({
  passed,
  failed,
}) => {
  const total = passed + failed;
  const center = RING_SIZE / 2;
  const segments = [
    { count: passed, color: "#16a34a" },
    { count: failed, color: "#dc2626" },
  ];

  let offset = 0;
  return (
    <svg
      width={RING_SIZE}
      height={RING_SIZE}
      viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
      className="shrink-0"
      aria-hidden="true"
    >
      <g transform={`rotate(-90 ${center} ${center})`}>
        {total === 0 ? (
          <circle
            cx={center}
            cy={center}
            r={RING_RADIUS}
            fill="none"
            stroke="#d1d5db"
            strokeWidth={RING_STROKE}
          />
        ) : (
          segments.map((segment, index) => {
            if (segment.count === 0) return null;
            const length = (segment.count / total) * RING_CIRCUMFERENCE;
            const dashOffset = -offset;
            offset += length;
            return (
              <circle
                key={index}
                cx={center}
                cy={center}
                r={RING_RADIUS}
                fill="none"
                stroke={segment.color}
                strokeWidth={RING_STROKE}
                strokeDasharray={`${length} ${RING_CIRCUMFERENCE - length}`}
                strokeDashoffset={dashOffset}
              />
            );
          })
        )}
      </g>
    </svg>
  );
};

interface OutcomeHealthCheckCardProps {
  healthCheck: PlaybookNodeSampleToolResultOutcomeHealthCheck;
}

export const OutcomeHealthCheckCard: FC<OutcomeHealthCheckCardProps> = ({
  healthCheck,
}) => {
  const { rows, passed, failed } = summarizeOutcomeHealthCheck(healthCheck);
  const [detailsRow, setDetailsRow] = useState<OutcomeHealthCheckRow | null>(
    null,
  );

  return (
    <>
      <div className="rounded-md bg-white shadow-sm ring-1 ring-black/5">
        <div className="flex items-center gap-3 px-4 py-3">
          <RingGlyph passed={passed} failed={failed} />
          <div className="flex-1">
            <p
              className={twMerge(
                "text-sm font-semibold",
                failed > 0 ? "text-red-600" : "text-green-700",
              )}
            >
              {failed > 0
                ? "Some checks were not successful"
                : "All checks passed"}
            </p>
            <p className="text-xs text-gray-500">
              {failed > 0
                ? `${passed} passed, ${failed} failed`
                : `${passed} passed`}
            </p>
          </div>
        </div>

        <div className="border-t border-gray-100">
          {rows.map((row) => {
            const menuItems: DropdownItem[] = [
              { label: "Show details", onClick: () => setDetailsRow(row) },
            ];
            return (
              <div
                key={row.key}
                className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5 last:border-b-0"
              >
                {statusIcon(row.status)}
                <span className="flex-1 text-sm font-medium text-gray-900">
                  {row.label}
                </span>
                <MenuTrigger>
                  <AriaButton
                    aria-label={`Details for ${row.label}`}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <IconDots className="size-5" />
                  </AriaButton>
                  <DropdownMenu items={menuItems} />
                </MenuTrigger>
              </div>
            );
          })}
        </div>
      </div>

      <Modal isOpen={detailsRow !== null} onCancel={() => setDetailsRow(null)}>
        {detailsRow ? (
          <>
            <ModalHeader isCloseVisible>{detailsRow.label}</ModalHeader>
            <div className="mt-5 text-sm whitespace-pre-wrap text-gray-800">
              {detailsRow.explanation}
            </div>
          </>
        ) : null}
      </Modal>
    </>
  );
};
